import React, { useState, useEffect } from "react";
import { useFormatter } from "./../../util/formatter";
import ReactEcharts from "echarts-for-react";
import moment from "moment-timezone";
import { Row, Col } from "react-bootstrap";
import "echarts/lib/chart/pie";
import "echarts/lib/component/title";
import "echarts/lib/component/tooltip";
import { dirname } from "../../util/resourcePath";
import { useSolarCalc } from "../../util/calculations";
import WindWaterSensors from "./WindWaterSensors";
import usePeakLabels from "../../util/peakLabels";
import MainSavings from "./MainSavings";
import MainSavingsEPC from "./MainSavingsEPC";

function DashboardMain(props) {
	const [country, setCountry] = useState(props.selectedSite.country);
	const [currency, setCurrency] = useState(props.selectedSite.currency);
	const [lastUpdated, setLastUpdated] = useState("");
	const [logo, setLogo] = useState("");
	const [pieChartConfig, setPieChartConfig] = useState({});
	const [selectedSite, setSelectedSite] = useState(props.selectedSite);
	const [senSavData, setSenSavData] = useState(null);
	const [isFeedinTariff, setIsFeedinTariff] = useState(false);
	const [isEPC, setIsEPC] = useState(false);
	const [todayData, setTodayData] = useState({});
	const [yesterdayData, setYesterdayData] = useState({});
	const [WWsensors, setWWsensors] = useState(null);

	const isFushan = useState(selectedSite.id == 284 ? true : false); // isFushan client

	const peakLabel = usePeakLabels(selectedSite.country);
	const solarCalc = useSolarCalc();
	const [formatNumber] = useFormatter();

	const directory = dirname();

	const bottomBorder = {
		borderBottom: "1px solid #666",
	};

	const windStyle = {
		color: "#35c099",
		fontWeight: "bold",
	};

	const waterStyle = {
		color: "#46badf",
		fontWeight: "bold",
	};

	/**
	 * Calculate results based on formula passed
	 * @param {string} formula
	 * @param {obj} selectedSite
	 * @param {obj} senSavData
	 * @returns calculated results
	 */
	const calculateFormula = (formula, selectedSite, senSavData) => {
		let answer = 0;
		switch (formula) {
			case "CO2_TREES":
				const co2 = Math.round(todayData.solarTotal * 0.505);
				const trees = Math.round(co2 / 22.5);
				answer = [co2, trees];
				break;

			case "SAVING":
				const prod = {
					ON: todayData.solarON,
					OFF: todayData.solarOFF,
					TOTAL: todayData.solarTotal,
				};
				const price = {
					ON: selectedSite.price_on,
					OFF: selectedSite.price_off,
					FT: selectedSite.ft_cost,
				};
				const discount = {
					ON: selectedSite.discount_on,
					OFF: selectedSite.discount_off,
					FT: selectedSite.ft_discount,
				};

				const currency = selectedSite.currency;

				const savingsObj = solarCalc.calculateSavings(
					prod,
					price,
					discount,
					currency
				);
				answer = savingsObj.savingsTotal;
				break;

			case "ENERGY":
				answer = Math.round(
					todayData.solarOFF * selectedSite.price_off +
					todayData.solarON * selectedSite.price_on
				);
				break;

			case "SOLAR_CONSUMPTION":
				answer = formatNumber(
					(senSavData.solar_power /
						(senSavData.solar_power + senSavData.grid_power)) *
					100,
					2
				);
				break;

			case "TOTAL_SOLAR_PROD":
				answer = Math.round(senSavData.totalSolarProd);

				break;

			case "TOTAL_GRID_PROD":
				answer = Math.round(
					senSavData.grid_total - selectedSite.grid_start_index
				);
				break;
			default:
				break;
		}

		if (Number.isNaN(answer)) {
			answer = "-";
		}

		return answer;
	};

	/**
	 * Format Sensor object
	 * @param {array} arr
	 * @returns Formatted sensor object
	 */
	const formatSensorObj = (arr) => {
		const key = arr[0];
		const value = arr[1];

		if (key === "wind_speed")
			return {
				label: "Wind Speed",
				value: `${value} m/s`,
				style: windStyle,
			};
		else
			return {
				label: "Water Level",
				value: `${value} m`,
				style: waterStyle,
			};
	};

	useEffect(() => {
		setTodayData(props.data.todayData);
		setYesterdayData(props.data.yesterdayData);
		setSenSavData(props.data.senSavData);
	}, [props.data]);

	useEffect(() => {
		setSelectedSite(props.selectedSite);
	}, [props.selectedSite]);

	useEffect(() => {
		if (!props.isEmpty(selectedSite) || selectedSite != null) {
			const contractID = selectedSite.contract_id;

			setIsFeedinTariff(contractID === "FIT");
			setIsEPC(contractID === "EPC");

			const slug = selectedSite.slug;
			setLogo(`${directory}/images/${slug}_logo.png`);

			setCurrency(props.selectedSite.currency);
			setCountry(props.selectedSite.country);
		}
	}, [selectedSite]);

	useEffect(() => {
		setLastUpdated("");

		if (senSavData != null || !props.isEmpty(senSavData)) {
			const now = moment();
			const dateData = senSavData.dateData;
			const diff = now.diff(dateData, "minutes");

			if (diff > 20) setLastUpdated(moment(dateData).format("DD/MM/YY HH:MM"));

			setPieChartConfig({
				color: ["#f28a10", "#86bc25"],
				tooltip: {
					trigger: "item",
					formatter: "{b}: {c} ({d}%)",
				},
				series: [
					{
						name: "Power Balance",
						type: "pie",
						radius: ["45%", "75%"],
						avoidLabelOverlap: false,
						label: {
							emphasis: {
								show: true,
								textStyle: {
									fontSize: "20",
									fontWeight: "bold",
								},
							},
						},
						labelLine: {
							normal: {
								show: true,
							},
						},
						data: [
							{
								value: [senSavData.grid_power],
								name: "Grid",
							},
							{
								value: [senSavData.solar_power],
								name: "Solar",
							},
						],
					},
				],
			});

			const entries = Object.entries(senSavData);
			const columnNames = ["wind_speed", "water_level"];

			const windWaterSensors = entries.filter((entry) =>
				columnNames.includes(entry[0])
			);

			const sensors = windWaterSensors.map((sensor) => formatSensorObj(sensor));

			if (sensors.length > 0) setWWsensors(sensors);
		}
	}, [senSavData]);

	return (
		<>
			{senSavData != null && selectedSite != null ? (
				<>
					<div id="main-dash" className="container-fluid">
						<div
							className="row"
							style={{ paddingTop: "0px", textAlign: "center" }}
						>
							<Col md="4" style={{ paddingBottom: "5px" }}>
								<Row>
									<Col md="12">
										<h5>Sensors</h5>
									</Col>
								</Row>
								<Row style={{ paddingBottom: "5px", ...bottomBorder }}>
									<Col
										md="4"
										style={{
											borderRight: "1px solid #666",
										}}
									>
										<h6>Module Temp.</h6>
										<p style={{ color: "#ffc709", fontWeight: "bold" }}>
											{formatNumber(senSavData.module_temp, 2)}
											°C
										</p>
									</Col>
									<Col md="4">
										<h6>Ambient Temp.</h6>
										<p style={{ color: "#ffc709", fontWeight: "bold" }}>
											{formatNumber(senSavData.ambient_temp, 2)} °C
										</p>
									</Col>
									<Col
										md="4"
										style={{
											borderLeft: "1px solid #666",
										}}
									>
										<h6>Irradiance</h6>
										<p style={{ color: "#FF8500", fontWeight: "bold" }}>
											{formatNumber(senSavData.irradiance, 2)} W/m²
										</p>
									</Col>
								</Row>
								{WWsensors != null && <WindWaterSensors sensors={WWsensors} />}
							</Col>
							<Col md="4">
								<Col md="12">
									<img alt="logo" className="logo-single" src={logo} />
									<h3 className="site_name" style={{ textAlign: "center" }}>
										{selectedSite != null && selectedSite.site_name}
									</h3>
									<h5
										style={{
											textAlign: "center",
											color: "#FF8500",
											fontWeight: "bold",
										}}
									>
										Plant Capacity
									</h5>
									<h5
										style={{
											textAlign: "center",
											color: "#FF8500",
											fontWeight: "bold",
										}}
									>
										{selectedSite != null &&
											formatNumber(selectedSite.power, 2)}{" "}
										kWp
									</h5>
								</Col>
							</Col>
							<Col
								md="4"
								style={{ paddingBottom: "5px", borderBottom: "1px solid #666" }}
							>
								{!isEPC ? (
									<MainSavings
										calculateFormula={calculateFormula}
										selectedSite={selectedSite}
										senSavData={senSavData}
										currency={currency}
									/>
								) : (
									<MainSavingsEPC
										calculateFormula={calculateFormula}
										selectedSite={selectedSite}
										senSavData={senSavData}
									/>
								)}
							</Col>
						</div>
					</div>
					<div className="container-fluid">
						<div className="row power-prod">
							<div className="col-md-45">
								<Col md="4">
									<h6>Current Solar Power</h6>
									<h3 style={{ fontSize: "34px" }}>
										<b>
											{formatNumber(senSavData.solar_power) < 0
												? 0
												: formatNumber(senSavData.solar_power)}{" "}
											kW
										</b>
									</h3>
									{lastUpdated != "" && (
										<p style={{ color: "red" }}>
											(Last updated: {lastUpdated})
										</p>
									)}
								</Col>
								<Col md="8" className="energy-left">
									<h5
										className="energy_sub_header"
										style={{ textAlign: "left" }}
									>
										Solar Energy
									</h5>
									<p>
										Today: <b>{formatNumber(todayData.solarTotal)} kWh</b>
									</p>
									<p>
										{peakLabel[0] + ": "}
										<b>{formatNumber(todayData.solarON)} kWh</b>
										{" | "}
										{peakLabel[1] + ": "}
										<b>{formatNumber(todayData.solarOFF)} kWh</b>
									</p>
									{!isEPC && (
										<p>
											Value of energy produced today:{" "}
											<b>
												{selectedSite != null
													? formatNumber(
														calculateFormula(
															"ENERGY",
															selectedSite,
															senSavData
														)
													)
													: ""}{" "}
												{" " + currency}
											</b>
										</p>
									)}
									<p>
										Yesterday:{" "}
										<b>{formatNumber(yesterdayData.solarTotal)} kWh</b>
									</p>
									<p>
										{peakLabel[0] + ": "}
										<b>{formatNumber(yesterdayData.solarON)} kWh</b>
										{" | "}
										{peakLabel[1] + ": "}
										<b>{formatNumber(yesterdayData.solarOFF)} kWh</b>
									</p>
									<p>
										Total:{" "}
										<b>
											{senSavData != null
												? formatNumber(
													calculateFormula(
														"TOTAL_SOLAR_PROD",
														selectedSite,
														senSavData
													)
												)
												: ""}{" "}
											kWh
										</b>
									</p>
								</Col>
							</div>
							{isFushan[0] == true ? <div className="col-md-3 selfConsumption"></div> : (!isFeedinTariff ? (
								<div className="col-md-3 selfConsumption">
									<ReactEcharts
										style={{ height: "200px" }}
										option={pieChartConfig}
										notMerge={true}
										lazyUpdate={true}
									/>
									<h6 style={{ marginTop: "-40px" }}>
										Self consumption:{" "}
										<b>
											{senSavData != null
												? calculateFormula(
													"SOLAR_CONSUMPTION",
													selectedSite,
													senSavData
												)
												: ""}{" "}
											%
										</b>
									</h6>
								</div>
							) : null)}
							{!isFeedinTariff && (
								<div className="col-md-45">
									<Col md="4" className="energy-right">
										<div style={{ textAlign: "center" }}>
											<h6>Current Grid Power</h6>
											<h3 style={{ fontSize: "34px" }}>
												<b>
													{selectedSite != null &&
														formatNumber(senSavData.grid_power)}{" "}
													kW
												</b>
											</h3>
											{lastUpdated != "" && (
												<p style={{ color: "red" }}>
													(Last updated: {lastUpdated})
												</p>
											)}
										</div>
									</Col>
									<div className="col-md-8 energy-right">
										<div className="grid-right">
											<h5 className="energy_sub_header">
												Grid Energy (Indicative)
											</h5>
											<p>
												Today: <b>{formatNumber(todayData.gridTotal)} kWh</b>
											</p>
											{country == "TH" && (
												<p>
													{peakLabel[0] + ": "}
													<b>{formatNumber(todayData.gridON)} kWh</b>
													{" | "}
													{peakLabel[1] + ": "}
													<b>{formatNumber(todayData.gridOFF)} kWh</b>
												</p>
											)}
											<p>
												Yesterday:{" "}
												<b>{formatNumber(yesterdayData.gridTotal)} kWh</b>
											</p>
											{country == "TH" && (
												<p>
													{peakLabel[0] + ": "}
													<b>{formatNumber(yesterdayData.gridON)} kWh</b>
													{" | "}
													{peakLabel[1] + ": "}
													<b>{formatNumber(yesterdayData.gridOFF)} kWh</b>
												</p>
											)}
											<p>
												Total:{" "}
												<b>
													{senSavData != null
														? formatNumber(
															calculateFormula(
																"TOTAL_GRID_PROD",
																selectedSite,
																senSavData
															)
														)
														: ""}{" "}
													kWh
												</b>
											</p>
										</div>
									</div>
								</div>
							)}
						</div>
					</div>
				</>
			) : (
				<></>
			)}
		</>
	);
}

export default DashboardMain;
